import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Save, Trash2, LogOut, LogIn, Users, UserPlus, Plus, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, fmt, typeBadge, todayISO } from "../lib/dimsum";

const MODES = [
  { id: "day", label: "Per Hari", testid: "laporan-mode-day" },
  { id: "range", label: "Rentang Tanggal", testid: "laporan-mode-range" },
  { id: "all", label: "Keseluruhan", testid: "laporan-mode-all" },
];

const emptyLine = () => ({ dimsum_type: "", pax: "" });
const emptyForm = () => ({ customer_name: "", lines: [emptyLine()] });

export const Laporan = ({ refreshKey, isAdmin, adminUser }) => {
  const isMaster = adminUser?.role === "master";
  const [mode, setMode] = useState("day");
  const [on, setOn] = useState(todayISO());
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [entries, setEntries] = useState([]);
  const [recaps, setRecaps] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editVals, setEditVals] = useState({});
  const [savingOverride, setSavingOverride] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [custDialog, setCustDialog] = useState(false);
  const [newCust, setNewCust] = useState("");
  const [showSug, setShowSug] = useState(false);
  const [lineSug, setLineSug] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [savingEntry, setSavingEntry] = useState(false);

  const loadReport = useCallback(() => {
    const params = new URLSearchParams({ mode });
    if (mode === "day") params.set("on", on);
    if (mode === "range") {
      params.set("start", start);
      params.set("end", end);
    }
    api.get(`/stock-report?${params.toString()}`).then((r) => setRows(r.data.rows || []));
  }, [mode, on, start, end]);

  const loadEntries = useCallback(() => {
    const params = new URLSearchParams();
    if (mode === "day") params.set("on", on);
    else if (mode === "range") {
      params.set("start", start);
      params.set("end", end);
    }
    api.get(`/stock-out?${params.toString()}`).then((r) => setEntries(r.data || []));
  }, [mode, on, start, end]);

  const loadRecaps = useCallback(() => {
    const params = new URLSearchParams();
    if (mode === "day") {
      params.set("start", on);
      params.set("end", on);
    } else if (mode === "range") {
      params.set("start", start);
      params.set("end", end);
    }
    api.get(`/recaps?${params.toString()}`).then((r) => setRecaps(r.data || []));
  }, [mode, on, start, end]);

  const loadCustomers = useCallback(() => {
    api.get("/customers").then((r) => setCustomers(r.data || []));
  }, []);

  useEffect(() => {
    loadReport();
    loadEntries();
    loadRecaps();
  }, [loadReport, loadEntries, loadRecaps, refreshKey]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const canEdit = mode === "day";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Wajib memilih nama pelanggan");
    const prepared = [];
    for (const l of form.lines) {
      const typed = (l.dimsum_type || "").trim();
      if (!typed && !l.pax) continue;
      const code = rows.find((r) => r.code.toLowerCase() === typed.toLowerCase())?.code;
      if (!code) return toast.error(`Jenis dimsum "${typed}" tidak valid`);
      if (Number(l.pax) <= 0) return toast.error("Jumlah pcs harus lebih dari 0");
      prepared.push({ dimsum_type: code, pax: Number(l.pax) });
    }
    if (prepared.length === 0)
      return toast.error("Isi minimal 1 jenis dimsum dengan jumlah pcs > 0");
    setSaving(true);
    try {
      await Promise.all(
        prepared.map((l) =>
          api.post("/stock-out", {
            date: on,
            customer_name: form.customer_name.trim(),
            dimsum_type: l.dimsum_type,
            pax: l.pax,
          })
        )
      );
      toast.success("Barang keluar tersimpan");
      setForm(emptyForm());
      loadReport();
      loadEntries();
    } catch {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const setLine = (i, patch) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
    }));

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));

  const removeLine = (i) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.length > 1 ? f.lines.filter((_, idx) => idx !== i) : f.lines,
    }));

  const remove = async (id) => {
    await api.delete(`/stock-out/${id}`);
    toast.success("Data dihapus");
    loadReport();
    loadEntries();
  };

  const saveEntryEdit = async () => {
    if (!editEntry) return;
    if (!editEntry.customer_name.trim()) return toast.error("Nama pelanggan wajib diisi");
    if (!editEntry.dimsum_type) return toast.error("Pilih jenis dimsum");
    if (Number(editEntry.pax) <= 0) return toast.error("Jumlah pcs harus lebih dari 0");
    setSavingEntry(true);
    try {
      await api.put(`/stock-out/${editEntry.id}`, {
        date: editEntry.date,
        customer_name: editEntry.customer_name.trim(),
        dimsum_type: editEntry.dimsum_type,
        pax: Number(editEntry.pax),
      });
      toast.success("Barang keluar diperbarui");
      setEditEntry(null);
      loadReport();
      loadEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan (khusus master)");
    } finally {
      setSavingEntry(false);
    }
  };

  const addCustomer = async () => {
    const name = newCust.trim();
    if (!name) return toast.error("Nama pelanggan wajib diisi");
    await api.post("/customers", { name });
    toast.success("Pelanggan ditambahkan");
    setNewCust("");
    loadCustomers();
  };

  const removeCustomer = async (id) => {
    await api.delete(`/customers/${id}`);
    loadCustomers();
  };

  const nameOf = (code) => rows.find((r) => r.code === code)?.name || code;

  const canEditReport = isMaster && mode === "day";

  const startEdit = () => {
    const v = {};
    rows.forEach((r) => {
      v[r.code] = { awal: r.awal, masuk: r.masuk, keluar: r.keluar };
    });
    setEditVals(v);
    setEditMode(true);
  };

  const setEditVal = (code, field, val) =>
    setEditVals((s) => ({ ...s, [code]: { ...s[code], [field]: val } }));

  const saveOverride = async () => {
    setSavingOverride(true);
    try {
      await Promise.all(
        rows.map((r) => {
          const e = editVals[r.code] || {};
          return api.post("/stock-override", {
            date: on,
            dimsum_type: r.code,
            awal: Number(e.awal) || 0,
            masuk: Number(e.masuk) || 0,
            keluar: Number(e.keluar) || 0,
          });
        })
      );
      toast.success("Laporan diperbarui");
      setEditMode(false);
      loadReport();
    } catch {
      toast.error("Gagal menyimpan perubahan");
    } finally {
      setSavingOverride(false);
    }
  };

  const resetOverride = async () => {
    setSavingOverride(true);
    try {
      await api.delete(`/stock-override?date=${on}`);
      toast.success("Dikembalikan ke otomatis");
      setEditMode(false);
      loadReport();
    } catch {
      toast.error("Gagal reset");
    } finally {
      setSavingOverride(false);
    }
  };

  const totals = rows.reduce(
    (a, r) => {
      a.awal += r.awal;
      a.masuk += r.masuk;
      a.keluar += r.keluar;
      a.sisa += r.sisa;
      return a;
    },
    { awal: 0, masuk: 0, keluar: 0, sisa: 0 }
  );

  const customerRecap = Object.values(
    entries.reduce((acc, it) => {
      const key = (it.customer_name || "").trim() || "Tanpa Nama";
      if (!acc[key]) acc[key] = { name: key, pcs: 0, trx: 0 };
      acc[key].pcs += Number(it.pax) || 0;
      acc[key].trx += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.pcs - a.pcs);

  const deleteMasukGroup = async (g) => {
    if (!window.confirm(`Hapus data masuk (input rekap) dari ${g.name}?`)) return;
    try {
      await Promise.all((g.recapIds || []).map((id) => api.delete(`/recaps/${id}`)));
      toast.success("Data masuk dihapus");
      loadRecaps();
      loadReport();
    } catch {
      toast.error("Gagal menghapus (butuh login admin/master)");
    }
  };

  const groupedEntries = Object.values(
    entries.reduce((acc, it) => {
      const key = (it.customer_name || "").trim() || "Tanpa Nama";
      (acc[key] = acc[key] || { name: key, items: [] }).items.push(it);
      return acc;
    }, {})
  );

  const groupedMasuk = Object.values(
    recaps.reduce((acc, r) => {
      const key = (r.members || []).join(" & ") || "Tanpa Nama";
      const g = (acc[key] = acc[key] || { name: key, items: [], recapIds: [] });
      if (!g.recapIds.includes(r.id)) g.recapIds.push(r.id);
      (r.items || []).forEach((it) =>
        g.items.push({
          id: `${r.id}-${it.dimsum_type}`,
          dimsum_type: it.dimsum_type,
          pax: it.pax,
        })
      );
      return acc;
    }, {})
  );
  const totalMasukPax = recaps.reduce(
    (s, r) => s + (r.items || []).reduce((a, it) => a + (Number(it.pax) || 0), 0),
    0
  );

  const q = form.customer_name.trim().toLowerCase();
  const customerSuggestions = q
    ? customers.filter((c) => c.name.toLowerCase().startsWith(q) && c.name.toLowerCase() !== q)
    : customers;

  return (
    <div className="space-y-6" data-testid="laporan-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-stone-900 flex items-center gap-2.5">
          <ClipboardList className="w-7 h-7 text-rose-600" /> Laporan Stok Dimsum
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1.5 bg-stone-100 rounded-2xl p-1.5">
            {MODES.map((m) => (
              <button
                key={m.id}
                data-testid={m.testid}
                onClick={() => setMode(m.id)}
                className={`min-h-[44px] rounded-xl px-4 font-bold text-sm transition-colors ${
                  mode === m.id ? "bg-white text-rose-600 shadow-sm" : "text-stone-600"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {isAdmin && (
          <button
            data-testid="add-customer-btn"
            onClick={() => setCustDialog(true)}
            className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-rose-600 px-4 font-bold text-sm text-white shadow-md hover:bg-rose-700 transition-colors"
          >
            <UserPlus className="w-5 h-5" /> Tambah Pelanggan
          </button>
          )}
        </div>
      </div>

      {mode === "day" && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-bold text-stone-600">Tanggal</label>
          <input
            type="date"
            data-testid="laporan-date-day"
            value={on}
            onChange={(e) => setOn(e.target.value)}
            className="min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
          />
        </div>
      )}
      {mode === "range" && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-bold text-stone-600">Dari</label>
          <input
            type="date"
            data-testid="laporan-date-start"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
          />
          <label className="text-sm font-bold text-stone-600">Sampai</label>
          <input
            type="date"
            data-testid="laporan-date-end"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
          />
        </div>
      )}

      {isAdmin && (
      <>
      {/* Input Barang Keluar */}
      <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 shadow-sm">
        <h3 className="font-bold text-lg text-stone-900 mb-4 flex items-center gap-2">
          <LogOut className="w-5 h-5 text-rose-600" /> Input Barang Keluar
        </h3>
        {!canEdit && (
          <p className="text-sm font-semibold text-amber-700 mb-4">
            Barang keluar hanya dapat diinput pada mode "Per Hari".
          </p>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="relative max-w-md">
            <label className="text-sm font-bold text-stone-600 mb-1.5 block">
              Nama Pelanggan <span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              autoComplete="off"
              data-testid="keluar-customer-name"
              disabled={!canEdit}
              value={form.customer_name}
              onChange={(e) => {
                setForm((f) => ({ ...f, customer_name: e.target.value }));
                setShowSug(true);
              }}
              onFocus={() => setShowSug(true)}
              onBlur={() => setTimeout(() => setShowSug(false), 150)}
              placeholder="cth. Toko Berkah"
              className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold disabled:bg-stone-100"
            />
            {showSug && canEdit && customerSuggestions.length > 0 && (
              <ul
                data-testid="customer-suggestions"
                className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto rounded-xl border-2 border-stone-200 bg-white shadow-lg"
              >
                {customerSuggestions.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      data-testid={`customer-suggestion-${c.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setForm((f) => ({ ...f, customer_name: c.name }));
                        setShowSug(false);
                      }}
                      className="w-full text-left px-3 py-2 font-semibold text-stone-700 hover:bg-rose-50 transition-colors"
                    >
                      {c.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-3">
            <label className="text-sm font-bold text-stone-600 block">Jenis Dimsum & Jumlah pcs</label>
            {form.lines.map((line, i) => (
              <div key={i} data-testid={`keluar-line-${i}`} className="flex items-center gap-2 sm:gap-3">
                <div className="relative flex-1 min-w-0">
                  <input
                    type="text"
                    autoComplete="off"
                    data-testid={`keluar-dimsum-type-${i}`}
                    disabled={!canEdit}
                    value={line.dimsum_type}
                    onChange={(e) => {
                      setLine(i, { dimsum_type: e.target.value });
                      setLineSug(i);
                    }}
                    onFocus={() => setLineSug(i)}
                    onBlur={() => setTimeout(() => setLineSug(null), 150)}
                    placeholder="Ketik jenis dimsum…"
                    className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-2 sm:px-3 font-semibold disabled:bg-stone-100"
                  />
                  {lineSug === i && canEdit && (() => {
                    const q = (line.dimsum_type || "").trim().toLowerCase();
                    const opts = rows.filter(
                      (r) => !q || r.name.toLowerCase().startsWith(q)
                    );
                    if (opts.length === 0) return null;
                    return (
                      <ul
                        data-testid={`dimsum-suggestions-${i}`}
                        className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto rounded-xl border-2 border-stone-200 bg-white shadow-lg"
                      >
                        {opts.map((r) => (
                          <li key={r.code}>
                            <button
                              type="button"
                              data-testid={`dimsum-suggestion-${i}-${r.code}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setLine(i, { dimsum_type: r.code });
                                setLineSug(null);
                                setTimeout(() => {
                                  const el = document.querySelector(
                                    `[data-testid="keluar-pcs-${i}"]`
                                  );
                                  if (el) el.focus();
                                }, 0);
                              }}
                              className="w-full text-left px-3 py-2 font-semibold text-stone-700 hover:bg-rose-50 transition-colors"
                            >
                              {r.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
                <input
                  type="number"
                  min="0"
                  data-testid={`keluar-pcs-${i}`}
                  disabled={!canEdit}
                  value={line.pax}
                  onChange={(e) => setLine(i, { pax: e.target.value })}
                  placeholder="pcs"
                  className="w-20 sm:w-28 shrink-0 min-h-[44px] rounded-xl border-2 border-stone-200 px-2 sm:px-3 font-mono-num font-semibold disabled:bg-stone-100"
                />
                <button
                  type="button"
                  data-testid={`keluar-line-remove-${i}`}
                  disabled={!canEdit || form.lines.length === 1}
                  onClick={() => removeLine(i)}
                  className="shrink-0 inline-grid place-items-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl text-rose-600 hover:bg-rose-50 disabled:opacity-30 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="keluar-add-line"
              disabled={!canEdit}
              onClick={addLine}
              className="inline-flex items-center gap-2 min-h-[40px] rounded-xl border-2 border-dashed border-stone-300 px-4 font-bold text-sm text-stone-600 hover:border-rose-400 hover:text-rose-600 disabled:opacity-40 transition-colors"
            >
              <Plus className="w-4 h-4" /> Tambah Jenis Dimsum
            </button>
          </div>

          <button
            type="submit"
            data-testid="keluar-save-btn"
            disabled={!canEdit || saving}
            className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl bg-rose-600 px-6 font-bold text-white shadow-md disabled:opacity-40 hover:bg-rose-700 transition-colors"
          >
            <Save className="w-5 h-5" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </form>
      </div>
      </>
      )}

      {(canEditReport || editMode) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="laporan-edit-toolbar">
          {!editMode ? (
            <button
              data-testid="laporan-edit-btn"
              onClick={startEdit}
              className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-stone-800 hover:bg-stone-900 text-white font-bold px-4 text-sm transition-colors"
            >
              Edit Laporan (Awal/Masuk/Keluar)
            </button>
          ) : (
            <>
              <button
                data-testid="laporan-save-override-btn"
                onClick={saveOverride}
                disabled={savingOverride}
                className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 text-sm disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" /> {savingOverride ? "Menyimpan…" : "Simpan Perubahan"}
              </button>
              <button
                data-testid="laporan-reset-override-btn"
                onClick={resetOverride}
                disabled={savingOverride}
                className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold px-4 text-sm disabled:opacity-50 transition-colors"
              >
                Reset Otomatis
              </button>
              <button
                data-testid="laporan-cancel-override-btn"
                onClick={() => setEditMode(false)}
                disabled={savingOverride}
                className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold px-4 text-sm disabled:opacity-50 transition-colors"
              >
                Batal
              </button>
            </>
          )}
        </div>
      )}

      <div className="bg-white border-2 border-stone-200 rounded-3xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs sm:text-base" data-testid="laporan-table">
            <thead className="bg-stone-50 border-b-2 border-stone-200">
              <tr className="text-stone-500 text-xs sm:text-sm font-bold">
                <th className="py-3 px-2 sm:px-4">Nama Jenis Dimsum</th>
                <th className="py-3 px-2 sm:px-4 text-right">Awal</th>
                <th className="py-3 px-2 sm:px-4 text-right">Masuk</th>
                <th className="py-3 px-2 sm:px-4 text-right">Keluar</th>
                <th className="py-3 px-2 sm:px-4 text-right">Sisa</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {rows.map((r) => (
                <tr key={r.code} data-testid={`laporan-row-${r.code}`} className="hover:bg-stone-50">
                  <td className="py-2.5 px-2 sm:px-4">
                    <span
                      className={`inline-block rounded-lg border px-2 py-0.5 sm:py-1 text-xs sm:text-sm font-bold ${typeBadge(
                        r.code
                      )}`}
                    >
                      {r.name}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 sm:px-4 text-right font-mono-num font-semibold text-stone-700">
                    {editMode ? (
                      <input
                        type="number"
                        data-testid={`laporan-edit-awal-${r.code}`}
                        value={editVals[r.code]?.awal ?? 0}
                        onChange={(e) => setEditVal(r.code, "awal", e.target.value)}
                        className="w-20 rounded-lg border-2 border-stone-200 px-2 py-1 text-right font-mono-num"
                      />
                    ) : (
                      fmt(r.awal)
                    )}
                  </td>
                  <td className="py-2.5 px-2 sm:px-4 text-right font-mono-num font-semibold text-emerald-700">
                    {editMode ? (
                      <input
                        type="number"
                        data-testid={`laporan-edit-masuk-${r.code}`}
                        value={editVals[r.code]?.masuk ?? 0}
                        onChange={(e) => setEditVal(r.code, "masuk", e.target.value)}
                        className="w-20 rounded-lg border-2 border-stone-200 px-2 py-1 text-right font-mono-num"
                      />
                    ) : (
                      fmt(r.masuk)
                    )}
                  </td>
                  <td className="py-2.5 px-2 sm:px-4 text-right font-mono-num font-semibold text-rose-700">
                    {editMode ? (
                      <input
                        type="number"
                        data-testid={`laporan-edit-keluar-${r.code}`}
                        value={editVals[r.code]?.keluar ?? 0}
                        onChange={(e) => setEditVal(r.code, "keluar", e.target.value)}
                        className="w-20 rounded-lg border-2 border-stone-200 px-2 py-1 text-right font-mono-num"
                      />
                    ) : (
                      fmt(r.keluar)
                    )}
                  </td>
                  <td className="py-2.5 px-2 sm:px-4 text-right font-mono-num font-extrabold text-stone-900">
                    {editMode
                      ? fmt(
                          (Number(editVals[r.code]?.awal) || 0) +
                            (Number(editVals[r.code]?.masuk) || 0) -
                            (Number(editVals[r.code]?.keluar) || 0)
                        )
                      : fmt(r.sisa)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-stone-50 border-t-2 border-stone-200">
              <tr className="font-extrabold text-stone-900">
                <td className="py-3 px-2 sm:px-4">Total</td>
                <td className="py-3 px-2 sm:px-4 text-right font-mono-num" data-testid="laporan-total-awal">
                  {fmt(totals.awal)}
                </td>
                <td className="py-3 px-2 sm:px-4 text-right font-mono-num" data-testid="laporan-total-masuk">
                  {fmt(totals.masuk)}
                </td>
                <td className="py-3 px-2 sm:px-4 text-right font-mono-num" data-testid="laporan-total-keluar">
                  {fmt(totals.keluar)}
                </td>
                <td className="py-3 px-2 sm:px-4 text-right font-mono-num" data-testid="laporan-total-sisa">
                  {fmt(totals.sisa)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {groupedMasuk.length > 0 && (
        <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 shadow-sm" data-testid="masuk-report-card">
          <h3 className="font-bold text-lg text-stone-900 mb-4 flex items-center gap-2">
            <LogIn className="w-5 h-5 text-emerald-600" /> Laporan Barang Masuk
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm" data-testid="masuk-report-table">
              <thead className="text-stone-500 font-bold border-b-2 border-stone-200">
                <tr>
                  <th className="py-2 px-2 sm:px-3">Nama Peserta</th>
                  <th className="py-2 px-2 sm:px-3">Jenis barang masuk</th>
                  <th className="py-2 px-2 sm:px-3 text-right">Jumlah pax barang masuk</th>
                </tr>
              </thead>
              <tbody>
                {groupedMasuk.map((g) => (
                  <React.Fragment key={g.name}>
                    {g.items.map((it, idx) => (
                      <tr
                        key={it.id}
                        data-testid={`masuk-report-row-${it.id}`}
                        className={idx === 0 ? "border-t-4 border-stone-200" : "border-t border-stone-100"}
                      >
                        <td className="py-2 px-2 sm:px-3 font-semibold text-stone-800">
                          {idx === 0 ? g.name : ""}
                        </td>
                        <td className="py-2 px-2 sm:px-3">
                          <span
                            className={`inline-block rounded-lg border px-2 py-0.5 font-bold ${typeBadge(
                              it.dimsum_type
                            )}`}
                          >
                            {nameOf(it.dimsum_type)}
                          </span>
                        </td>
                        <td className="py-2 px-2 sm:px-3 text-right font-mono-num font-bold text-emerald-700">
                          {fmt(it.pax)}
                        </td>
                      </tr>
                    ))}
                    <tr
                      data-testid={`masuk-report-subtotal-${g.name}`}
                      className="bg-stone-50 font-bold text-stone-800 border-t border-stone-200"
                    >
                      <td className="py-2 px-2 sm:px-3" colSpan={2}>
                        <div className="flex items-center gap-2">
                          <span>Subtotal {g.name}</span>
                          {isAdmin && (
                            <button
                              data-testid={`masuk-delete-${g.name}`}
                              onClick={() => deleteMasukGroup(g)}
                              className="grid place-items-center w-7 h-7 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors no-print"
                              title="Hapus data masuk peserta ini"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 sm:px-3 text-right font-mono-num text-emerald-700">
                        {fmt(g.items.reduce((s, it) => s + (Number(it.pax) || 0), 0))}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-stone-200 bg-stone-50">
                <tr className="font-extrabold text-stone-900">
                  <td className="py-2.5 px-2 sm:px-3" colSpan={2}>
                    Total pax barang masuk
                  </td>
                  <td
                    data-testid="masuk-report-total"
                    className="py-2.5 px-2 sm:px-3 text-right font-mono-num"
                  >
                    {fmt(totalMasukPax)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 shadow-sm" data-testid="keluar-report-card">
          <h3 className="font-bold text-lg text-stone-900 mb-4 flex items-center gap-2">
            <LogOut className="w-5 h-5 text-rose-600" /> Laporan Barang Keluar
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm" data-testid="keluar-report-table">
              <thead className="text-stone-500 font-bold border-b-2 border-stone-200">
                <tr>
                  <th className="py-2 px-2 sm:px-3">Nama Pelanggan</th>
                  <th className="py-2 px-2 sm:px-3">Jenis barang keluar</th>
                  <th className="py-2 px-2 sm:px-3 text-right">Jumlah pax barang keluar</th>
                  {isAdmin && <th className="py-2 px-2 sm:px-3 text-right no-print">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {groupedEntries.map((g) => (
                  <React.Fragment key={g.name}>
                    {g.items.map((it, idx) => (
                      <tr
                        key={it.id}
                        data-testid={`keluar-report-row-${it.id}`}
                        className={idx === 0 ? "border-t-4 border-stone-200" : "border-t border-stone-100"}
                      >
                        <td className="py-2 px-2 sm:px-3 font-semibold text-stone-800">
                          {idx === 0 ? g.name : ""}
                        </td>
                        <td className="py-2 px-2 sm:px-3">
                          <span
                            className={`inline-block rounded-lg border px-2 py-0.5 font-bold ${typeBadge(
                              it.dimsum_type
                            )}`}
                          >
                            {nameOf(it.dimsum_type)}
                          </span>
                        </td>
                        <td className="py-2 px-2 sm:px-3 text-right font-mono-num font-bold text-rose-700">
                          {fmt(it.pax)}
                        </td>
                        {isAdmin && (
                          <td className="py-2 px-2 sm:px-3 text-right no-print">
                            <div className="flex items-center justify-end gap-1">
                              {isMaster && (
                                <button
                                  data-testid={`keluar-edit-${it.id}`}
                                  onClick={() => setEditEntry({ ...it })}
                                  className="inline-grid place-items-center w-9 h-9 rounded-lg text-sky-600 hover:bg-sky-50 transition-colors"
                                  title="Edit entri barang keluar"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                data-testid={`keluar-delete-${it.id}`}
                                onClick={() => remove(it.id)}
                                className="inline-grid place-items-center w-9 h-9 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr
                      data-testid={`keluar-report-subtotal-${g.name}`}
                      className="bg-stone-50 font-bold text-stone-800 border-t border-stone-200"
                    >
                      <td className="py-2 px-2 sm:px-3" colSpan={2}>
                        Subtotal {g.name}
                      </td>
                      <td className="py-2 px-2 sm:px-3 text-right font-mono-num text-rose-700">
                        {fmt(g.items.reduce((s, it) => s + (Number(it.pax) || 0), 0))}
                      </td>
                      {isAdmin && <td className="no-print"></td>}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-stone-200 bg-stone-50">
                <tr className="font-extrabold text-stone-900">
                  <td className="py-2.5 px-2 sm:px-3" colSpan={2}>
                    Total pax barang keluar
                  </td>
                  <td
                    data-testid="keluar-report-total"
                    className="py-2.5 px-2 sm:px-3 text-right font-mono-num"
                  >
                    {fmt(entries.reduce((s, it) => s + (Number(it.pax) || 0), 0))}
                  </td>
                  {isAdmin && <td className="no-print"></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {customerRecap.length > 0 && (
        <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 shadow-sm" data-testid="customer-recap-card">
          <h3 className="font-bold text-lg text-stone-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-rose-600" /> Rekap per Pelanggan
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="customer-recap-table">
              <thead className="text-stone-500 font-bold border-b border-stone-200">
                <tr>
                  <th className="py-2 px-3">Nama Pelanggan</th>
                  <th className="py-2 px-3 text-right">Transaksi</th>
                  <th className="py-2 px-3 text-right">Total pcs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {customerRecap.map((c) => (
                  <tr key={c.name} data-testid={`customer-recap-row-${c.name}`}>
                    <td className="py-2 px-3 font-semibold text-stone-800">{c.name}</td>
                    <td className="py-2 px-3 text-right font-mono-num">{c.trx}</td>
                    <td className="py-2 px-3 text-right font-mono-num font-bold text-rose-700">
                      {fmt(c.pcs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={custDialog} onOpenChange={setCustDialog}>
        <DialogContent data-testid="add-customer-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Pelanggan</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <input
              type="text"
              data-testid="new-customer-name"
              value={newCust}
              onChange={(e) => setNewCust(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustomer()}
              placeholder="Nama pelanggan"
              className="flex-1 min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
            />
            <button
              data-testid="new-customer-save"
              onClick={addCustomer}
              className="inline-flex items-center gap-2 min-h-[44px] rounded-xl bg-rose-600 px-4 font-bold text-white hover:bg-rose-700 transition-colors"
            >
              <Save className="w-4 h-4" /> Simpan
            </button>
          </div>
          {customers.length > 0 && (
            <div className="max-h-64 overflow-y-auto divide-y divide-stone-100" data-testid="customer-master-list">
              {customers.map((c) => (
                <div key={c.id} className="flex items-center justify-between py-2">
                  <span className="font-semibold text-stone-800">{c.name}</span>
                  <button
                    data-testid={`customer-delete-${c.id}`}
                    onClick={() => removeCustomer(c.id)}
                    className="inline-grid place-items-center w-9 h-9 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent data-testid="edit-keluar-dialog">
          <DialogHeader>
            <DialogTitle>Edit Barang Keluar</DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-3">
              <div>
                <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Nama Pelanggan</label>
                <input
                  data-testid="edit-keluar-customer"
                  value={editEntry.customer_name}
                  onChange={(e) => setEditEntry((s) => ({ ...s, customer_name: e.target.value }))}
                  className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Jenis Dimsum</label>
                  <select
                    data-testid="edit-keluar-type"
                    value={editEntry.dimsum_type}
                    onChange={(e) => setEditEntry((s) => ({ ...s, dimsum_type: e.target.value }))}
                    className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold bg-white"
                  >
                    {rows.map((r) => (
                      <option key={r.code} value={r.code}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Jumlah (pcs)</label>
                  <input
                    type="number"
                    data-testid="edit-keluar-pax"
                    value={editEntry.pax}
                    onChange={(e) => setEditEntry((s) => ({ ...s, pax: e.target.value }))}
                    className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-mono-num font-bold"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  data-testid="edit-keluar-save"
                  onClick={saveEntryEdit}
                  disabled={savingEntry}
                  className="flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 transition-colors"
                >
                  <Save className="w-4 h-4" /> {savingEntry ? "Menyimpan…" : "Simpan"}
                </button>
                <button
                  onClick={() => setEditEntry(null)}
                  disabled={savingEntry}
                  className="min-h-[44px] px-4 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold transition-colors"
                >
                  Batal
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};
